import * as E from '@react-email/components'

const appName = ENV.APP_NAME
const appBaseUrl = ENV.APP_BASE_URL
const logoUrl = `${appBaseUrl}/img/logo/logo.png`

function EmailHeader() {
	return (
		<E.Section style={{ textAlign: 'center', marginBottom: '30px' }}>
			<E.Link href={appBaseUrl}>
				<E.Img
					src={logoUrl}
					alt={`${appName} logo`}
					width="120"
					style={{
						display: 'block',
						margin: '0 auto',
						maxWidth: '100%',
						height: 'auto',
					}}
				/>
			</E.Link>
		</E.Section>
	)
}

function EmailFooter() {
	return (
		<E.Section style={{ textAlign: 'center', marginTop: '30px' }}>
			<E.Text>© 2025 {appName}. All rights reserved.</E.Text>
		</E.Section>
	)
}

export function ForgotPasswordEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Reset your {appName} password</E.Preview>
			<E.Body
				style={{
					fontFamily: 'Arial, sans-serif',
					backgroundColor: '#f9f9f9',
					padding: '20px',
				}}
			>
				<E.Container
					style={{
						backgroundColor: '#fff',
						padding: '40px',
						borderRadius: '8px',
						maxWidth: '600px',
						margin: '0 auto',
					}}
				>
					<EmailHeader />

					<E.Heading
						as="h2"
						style={{ textAlign: 'center', marginBottom: '20px' }}
					>
						Reset your password
					</E.Heading>

					<E.Text
						style={{
							fontSize: '14px',
							lineHeight: '22px',
							marginBottom: '24px',
							textAlign: 'center',
						}}
					>
						Use the following verification code to reset your {appName}{' '}
						password:
					</E.Text>

					<E.Text
						style={{
							fontSize: '24px',
							fontWeight: 'bold',
							letterSpacing: '4px',
							textAlign: 'center',
							marginBottom: '30px',
						}}
					>
						{otp}
					</E.Text>

					<E.Button
						href={onboardingUrl}
						style={{
							backgroundColor: '#007bff',
							color: '#fff',
							padding: '12px 24px',
							borderRadius: '6px',
							textDecoration: 'none',
							fontWeight: 'bold',
							display: 'block',
							width: 'fit-content',
							margin: '0 auto 20px',
							fontSize: '14px',
						}}
					>
						Reset Password
					</E.Button>

					<E.Text
						style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}
					>
						Or paste this link into your browser:
						<br />
						<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
					</E.Text>

					<EmailFooter />
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export function VerificationCodeEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Your {appName} verification code</E.Preview>
			<E.Body
				style={{
					fontFamily: 'Arial, sans-serif',
					backgroundColor: '#f9f9f9',
					padding: '20px',
				}}
			>
				<E.Container
					style={{
						backgroundColor: '#fff',
						padding: '40px',
						borderRadius: '8px',
						maxWidth: '600px',
						margin: '0 auto',
					}}
				>
					<EmailHeader />

					<E.Heading
						as="h2"
						style={{ textAlign: 'center', marginBottom: '20px' }}
					>
						Welcome to {appName}!
					</E.Heading>

					<E.Text
						style={{
							fontSize: '14px',
							lineHeight: '22px',
							marginBottom: '24px',
							textAlign: 'center',
						}}
					>
						Use the verification code below to complete your sign-up:
					</E.Text>

					<E.Text
						style={{
							fontSize: '24px',
							fontWeight: 'bold',
							letterSpacing: '4px',
							textAlign: 'center',
							marginBottom: '30px',
						}}
					>
						{otp}
					</E.Text>

					<E.Button
						href={onboardingUrl}
						style={{
							backgroundColor: '#007bff',
							color: '#fff',
							padding: '12px 24px',
							borderRadius: '6px',
							textDecoration: 'none',
							fontWeight: 'bold',
							display: 'block',
							width: 'fit-content',
							margin: '0 auto 20px',
							fontSize: '14px',
						}}
					>
						Finish Sign Up
					</E.Button>

					<E.Text
						style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}
					>
						Or paste this link into your browser:
						<br />
						<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
					</E.Text>

					<EmailFooter />
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export function EmailChangeEmail({
	verifyUrl,
	otp,
}: {
	verifyUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Confirm your email change for {appName}</E.Preview>
			<E.Body
				style={{
					fontFamily: 'Arial, sans-serif',
					backgroundColor: '#f9f9f9',
					padding: '20px',
				}}
			>
				<E.Container
					style={{
						backgroundColor: '#fff',
						padding: '40px',
						borderRadius: '8px',
						maxWidth: '600px',
						margin: '0 auto',
					}}
				>
					<EmailHeader />

					<E.Heading
						as="h2"
						style={{ textAlign: 'center', marginBottom: '20px' }}
					>
						Confirm your email change
					</E.Heading>

					<E.Text
						style={{
							fontSize: '14px',
							lineHeight: '22px',
							marginBottom: '24px',
							textAlign: 'center',
						}}
					>
						Use the code below to verify your new email address:
					</E.Text>

					<E.Text
						style={{
							fontSize: '24px',
							fontWeight: 'bold',
							letterSpacing: '4px',
							textAlign: 'center',
							marginBottom: '30px',
						}}
					>
						{otp}
					</E.Text>

					<E.Button
						href={verifyUrl}
						style={{
							backgroundColor: '#007bff',
							color: '#fff',
							padding: '12px 24px',
							borderRadius: '6px',
							textDecoration: 'none',
							fontWeight: 'bold',
							display: 'block',
							width: 'fit-content',
							margin: '0 auto 20px',
							fontSize: '14px',
						}}
					>
						Confirm Email Change
					</E.Button>

					<E.Text
						style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}
					>
						Or paste this link into your browser:
						<br />
						<E.Link href={verifyUrl}>{verifyUrl}</E.Link>
					</E.Text>

					<EmailFooter />
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export function EmailChangeNoticeEmail({ userId }: { userId: string }) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Your {appName} email has been changed</E.Preview>
			<E.Body
				style={{
					fontFamily: 'Arial, sans-serif',
					backgroundColor: '#f9f9f9',
					padding: '20px',
				}}
			>
				<E.Container
					style={{
						backgroundColor: '#fff',
						padding: '40px',
						borderRadius: '8px',
						maxWidth: '600px',
						margin: '0 auto',
					}}
				>
					<EmailHeader />

					<E.Heading
						as="h2"
						style={{ textAlign: 'center', marginBottom: '20px' }}
					>
						Your email has been changed
					</E.Heading>

					<E.Text
						style={{
							fontSize: '14px',
							lineHeight: '22px',
							marginBottom: '20px',
							textAlign: 'center',
						}}
					>
						We're writing to let you know that your {appName} account email has
						been successfully changed.
					</E.Text>

					<E.Text
						style={{
							fontSize: '14px',
							lineHeight: '22px',
							marginBottom: '24px',
							textAlign: 'center',
						}}
					>
						If you made this change, no action is needed. If not, please contact
						our support team immediately.
					</E.Text>

					<E.Text
						style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}
					>
						Your Account ID: <strong>{userId}</strong>
					</E.Text>

					<EmailFooter />
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export function WelcomeEmail() {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>Thanks for joining the {appName} newsletter!</E.Preview>
			<E.Body
				style={{
					fontFamily: 'Arial, sans-serif',
					backgroundColor: '#f9f9f9',
					padding: '20px',
				}}
			>
				<E.Container
					style={{
						backgroundColor: '#fff',
						padding: '40px',
						borderRadius: '8px',
						maxWidth: '600px',
						margin: '0 auto',
					}}
				>
					<EmailHeader />

					<E.Heading
						as="h2"
						style={{ textAlign: 'center', marginBottom: '20px' }}
					>
						Welcome to the {appName} Newsletter!
					</E.Heading>

					<E.Text
						style={{
							fontSize: '14px',
							lineHeight: '22px',
							textAlign: 'center',
						}}
					>
						You're officially subscribed. Stay tuned for product updates, new
						features, and helpful tips delivered straight to your inbox.
					</E.Text>

					<EmailFooter />
				</E.Container>
			</E.Body>
		</E.Html>
	)
}

export function AppUpdateEmail({
	version,
	title,
	description,
}: {
	version: string
	title: string
	description: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Head />
			<E.Preview>New update v{version} is here – check what's new</E.Preview>
			<E.Body
				style={{
					fontFamily: 'Arial, sans-serif',
					backgroundColor: '#f9f9f9',
					padding: '20px',
				}}
			>
				<E.Container
					style={{
						backgroundColor: '#fff',
						padding: '40px',
						borderRadius: '8px',
						maxWidth: '600px',
						margin: '0 auto',
					}}
				>
					<EmailHeader />

					<E.Heading
						as="h2"
						style={{ marginBottom: '10px', textAlign: 'center' }}
					>
						🚀 New Update: v{version}
					</E.Heading>

					<E.Text
						style={{
							fontSize: '16px',
							lineHeight: '24px',
							marginBottom: '20px',
							textAlign: 'center',
						}}
					>
						<strong>{title}</strong>
					</E.Text>

					<E.Text
						style={{
							fontSize: '14px',
							lineHeight: '22px',
							marginBottom: '30px',
							textAlign: 'center',
						}}
					>
						{description}
					</E.Text>

					<E.Button
						href={`${appBaseUrl}/updates`}
						style={{
							backgroundColor: '#007bff',
							color: '#fff',
							padding: '12px 24px',
							borderRadius: '6px',
							textDecoration: 'none',
							fontWeight: 'bold',
							display: 'block',
							width: 'fit-content',
							margin: '0 auto 20px',
							fontSize: '14px',
							textAlign: 'center',
						}}
					>
						View All Updates
					</E.Button>

					<E.Text
						style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}
					>
						Or paste this link in your browser: {appBaseUrl}/updates
					</E.Text>

					<E.Hr style={{ margin: '40px 0' }} />

					<E.Text
						style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}
					>
						You are receiving this because you’re subscribed to {appName}{' '}
						updates.
					</E.Text>

					<EmailFooter />
				</E.Container>
			</E.Body>
		</E.Html>
	)
}
